/**
 * Who is signed in. The session itself is an HttpOnly cookie the browser holds
 * and this code never sees; all this knows is what /auth/me says.
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { SIGNED_OUT_EVENT, api, post } from './api';

export interface Me {
  /** Which sign-in vouched for this person: 'entra', 'dev' locally, or the retiring 'password'. */
  provider: string;
  /** Stable identifier. Shown only when there is no name or email. */
  subject: string;
  email: string | null;
  name: string | null;
  role: 'admin' | 'analyst';
}

/** How to show who is signed in: their name, else their email, else the identifier. */
export function displayName(me: Me): string {
  return me.name ?? me.email ?? me.subject;
}

interface AuthState {
  me: Me | null | undefined;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  // undefined = still finding out; null = signed out.
  const [me, setMe] = useState<Me | null | undefined>(undefined);

  useEffect(() => {
    api<Me>('/auth/me').then(setMe, () => setMe(null));
    const onSignedOut = () => setMe(null);
    window.addEventListener(SIGNED_OUT_EVENT, onSignedOut);
    return () => window.removeEventListener(SIGNED_OUT_EVENT, onSignedOut);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setMe(await post<Me>('/auth/login', { email, password }));
  }, []);

  const logout = useCallback(async () => {
    await post('/auth/logout').catch(() => undefined);
    setMe(null);
  }, []);

  return <AuthContext.Provider value={{ me, login, logout }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}

/** Protected routes: an unauthenticated deep link goes to sign-in and comes back afterwards. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { me } = useAuth();
  const location = useLocation();
  if (me === undefined) return null;
  if (me === null) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <>{children}</>;
}
